// Several accounts on one device (33-accounts.md, "One account at a time", "Adding", "The default
// account"): the waiting accounts' sessions beside the active one, each with its own databases;
// switching swaps them and reloads. Small on purpose: the start reads it before anything runs.
import { deleteAllDatabases } from '../core/device';
import { readJson, writeJson } from '../core/storage';
import type { MatrixClient } from 'matrix-js-sdk';
import { readSession, writeSession, type Session, type Who } from '../core/session';
import { reopen } from '../core/router';
import { startSwitching } from './switching';

export interface Account extends Session {
  /** The name of its databases (the sync store `<db>_sync`, the crypto stores `<db>::…`). */
  db: string;
  signed_out?: boolean;
}

const ACCOUNTS = 'desyr_accounts';
const PREFIX = 'desyr_store_prefix';
const DEFAULT = 'desyr_default_account';
const ADDING = 'desyr_adding_account';
const SWITCHED = 'desyr_switched';
export const UNLOCK_ASKED = 'desyr_unlock_asked';
/** The first account's names (90-test-hooks.md: `desyr_sync`, crypto prefix `desyr`). */
const FIRST = 'desyr';

const raw = (key: string, value?: string | null, store: Storage = localStorage) => {
  try {
    if (value === undefined) return store.getItem(key) ?? undefined;
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    // storage refused
  }
  return undefined;
};

export const waiting = (): Account[] => readJson<Account[]>(ACCOUNTS) ?? [];
/** The open account (through its client) and the waiting ones still signed in, in the switcher's order. */
export const everyAccount = (client: MatrixClient): Who[] => [{ user_id: client.getUserId()!, base_url: client.baseUrl, access_token: client.getAccessToken()! }, ...waiting().filter((w) => !w.signed_out)];
export const keepWaiting = (list: Account[]) => writeJson(ACCOUNTS, list.length ? list : undefined);
/** The active account's database name. */
export const storePrefix = () => raw(PREFIX) ?? FIRST;
export const defaultAccount = () => raw(DEFAULT);
export const setDefault = (userId: string | null) => raw(DEFAULT, userId);
export const adding = () => raw(ADDING);

/** Makes a waiting account the active one; the active one (if any) waits. */
function activate(to: Account): void {
  const current = readSession();
  const list = waiting().filter((a) => a.user_id !== to.user_id && a.user_id !== current?.user_id);
  if (current) list.push({ ...current, db: storePrefix() });
  keepWaiting(list);
  const { db, signed_out: _, ...session } = to;
  writeSession(session);
  raw(PREFIX, db);
}

/** Makes a waiting account the active one in this page, without a reload; false when there is none. */
export function activateAccount(userId: string): boolean {
  const to = waiting().find((a) => a.user_id === userId && !a.signed_out);
  if (to) activate(to);
  return !!to;
}

/** Switches this tab to a waiting account and reloads at the address to open; picked by the person (`circle`), its skeleton shows at once (33). */
export function switchTo(userId: string, path = '/', circle?: string): void {
  const to = waiting().find((a) => a.user_id === userId);
  if (!to) return;
  activate(to);
  raw(SWITCHED, '1', sessionStorage);
  // In this tab's entry, with the guard under the page (07, v1.58.27): accounts don't stack.
  if (circle) startSwitching(userId, circle, () => reopen(path));
  else reopen(path);
}

/**
 * Before anything reads the session (03, start step 1): the default account when this tab hasn't
 * switched; with nobody active (and nothing being added), the account that waited last.
 */
export function startAccounts(): void {
  const list = waiting().filter((a) => !a.signed_out);
  const preferred = !raw(SWITCHED, undefined, sessionStorage) && !adding() && list.find((a) => a.user_id === defaultAccount());
  if (preferred) activate(preferred);
  else if (!readSession() && !adding() && list.length) activate(list[list.length - 1]);
}

/** Add account: the open account waits, the sign-in page says "Add an account". */
/** Add account (33): the open account waits; the sign-in page (or `to`, a circle's own) adds the next one. */
export function startAdding(to = '/login'): void {
  const current = readSession();
  if (current) {
    keepWaiting([...waiting().filter((a) => a.user_id !== current.user_id), { ...current, db: storePrefix() }]);
    raw(ADDING, current.user_id);
  }
  raw('desyr_session', null);
  location.assign(to);
}

/** Cancel while adding: back to the account that was open. */
export function cancelAdding(): void {
  const back = adding();
  raw(ADDING, null);
  switchTo(back ?? '', '/');
}

/** An account's sync and crypto databases (matrix-js-sdk names them from its database name). */
export const deleteDatabasesOf = (db: string) => deleteAllDatabases((name) => name === `matrix-js-sdk:${db}_sync` || name.startsWith(`${db}::`));

/** A database name for a new account: by its device, which circles from desyr ID's sessions call share (36, Behaviour 6.2). */
export function freshDb(device: string, list = waiting()): string {
  const taken = new Set([storePrefix(), ...list.map((a) => a.db)]);
  let db = `${FIRST}-${device}`;
  for (let n = 2; taken.has(db); n++) db = `${FIRST}-${device}-${n}`;
  return db;
}

/** Right after signing in, Sessions may ask once to unlock the earlier messages (19): false until it has. */
export function askUnlock(userId: string): void {
  const asked = readJson<Record<string, boolean>>(UNLOCK_ASKED, sessionStorage) ?? {};
  if (!asked[userId]) writeJson(UNLOCK_ASKED, { ...asked, [userId]: false }, sessionStorage);
}

/** A sign-in finished: the new account gets its own databases and replaces a waiting entry of the same person. */
export function signedInAccount(session: Session): void {
  raw(ADDING, null);
  askUnlock(session.user_id);
  const list = waiting();
  const replaced = list.find((a) => a.user_id === session.user_id);
  const rest = list.filter((a) => a !== replaced);
  keepWaiting(rest);
  raw(PREFIX, rest.some((a) => a.db === FIRST) || replaced?.db === FIRST ? freshDb(session.device_id, rest) : FIRST);
  if (replaced) void deleteDatabasesOf(replaced.db).catch(() => {});
}
